import { Application, RequestHandler } from 'express';
import { Logger } from '@unidev-hub/logger';
import { ConfigManager } from '@unidev-hub/config';

/**
 * Middleware enum defining all available middleware names
 */
export enum Middleware {
  HELMET = 'helmet',
  CORS = 'cors',
  COMPRESSION = 'compression',
  BODY_PARSER = 'bodyParser',
  CORRELATION = 'correlation',
  LOGGING = 'logging',
  RESPONSE_FORMAT = 'responseFormat',
  RATE_LIMIT = 'rateLimit',
  METRICS = 'metrics',
  ERROR_HANDLING = 'errorHandling',
  AUTH = 'auth',
  VALIDATION = 'validation',
  UPLOAD = 'upload'
}

/**
 * Standard middleware configurations - Provides commonly used middleware combinations
 */
export const MiddlewareGroups = {
  /**
   * Default middleware stack for most applications
   */
  DEFAULT: [
    Middleware.CORS,
    Middleware.HELMET,
    Middleware.COMPRESSION,
    Middleware.BODY_PARSER,
    Middleware.CORRELATION,
    Middleware.LOGGING,
    Middleware.RESPONSE_FORMAT,
    Middleware.ERROR_HANDLING
  ],

  /**
   * Middleware stack for APIs with rate limiting and metrics
   */
  API: [
    Middleware.CORS,
    Middleware.HELMET,
    Middleware.COMPRESSION,
    Middleware.BODY_PARSER,
    Middleware.CORRELATION,
    Middleware.LOGGING,
    Middleware.RESPONSE_FORMAT,
    Middleware.RATE_LIMIT,
    Middleware.METRICS,
    Middleware.ERROR_HANDLING
  ],

  /**
   * Basic middleware stack with minimal processing
   */
  MINIMAL: [
    Middleware.CORS,
    Middleware.HELMET,
    Middleware.BODY_PARSER,
    Middleware.ERROR_HANDLING
  ]
};

/**
 * Correlation ID options
 */
export interface CorrelationIdOptions {
  /**
   * Header name for correlation ID
   * @default 'X-Correlation-ID'
   */
  header?: string;

  /**
   * Property name to store on request object
   * @default 'correlationId'
   */
  propertyName?: string;

  /**
   * Whether to generate a correlation ID if none is provided
   * @default true
   */
  generateId?: boolean;
}

/**
 * Error handling options
 */
export interface ErrorHandlingOptions {
  /**
   * Whether to include stack trace in error responses
   * @default true in development, false in production
   */
  includeStack?: boolean;

  /**
   * Whether to log errors
   * @default true
   */
  logErrors?: boolean;

  /**
   * Default error message
   * @default 'An unexpected error occurred'
   */
  defaultMessage?: string;

  /**
   * Default error code
   * @default 'INTERNAL_ERROR'
   */
  defaultCode?: string;
}

/**
 * Response formatting options
 */
export interface ResponseFormatOptions {
  /**
   * Whether to wrap successful responses
   * @default true
   */
  wrapSuccess?: boolean;

  /**
   * Whether to include request ID in response
   * @default true
   */
  includeRequestId?: boolean;

  /**
   * Default success message
   * @default 'Operation successful'
   */
  defaultSuccessMessage?: string;

  /**
   * Default error message
   * @default 'Operation failed'
   */
  defaultErrorMessage?: string;
}

/**
 * Rate limiting options
 */
export interface RateLimitOptions {
  /**
   * Time window in milliseconds
   * @default 900000 (15 minutes)
   */
  windowMs?: number;

  /**
   * Maximum number of requests per IP in the time window
   * @default 100
   */
  max?: number;

  /**
   * Whether to add standard rate limiting headers
   * @default true
   */
  standardHeaders?: boolean;

  /**
   * Message to send when rate limit is exceeded
   * @default 'Too many requests, please try again later.'
   */
  message?: string;

  /**
   * Skip rate limiting for certain requests (return true to skip)
   */
  skip?: (req: any) => boolean;

  /**
   * Additional options to pass to express-rate-limit
   */
  additionalOptions?: Record<string, any>;
}

/**
 * Metrics options
 */
export interface MetricsOptions {
  /**
   * Prefix for metric names
   * @default ''
   */
  prefix?: string;

  /**
   * Whether to collect default metrics (in framework)
   * @default true
   */
  defaultMetrics?: boolean;

  /**
   * Whether to collect default metrics (in custom middleware)
   * @default false
   */
  collectDefaults?: boolean;

  /**
   * Endpoint for metrics
   * @default '/metrics'
   */
  endpoint?: string;

  /**
   * Default labels to add to all metrics
   */
  defaultLabels?: Record<string, string>;

  enabled?: boolean;
}

/**
 * Request logger options
 */
export interface RequestLoggerOptions {
  /**
   * Skip logging for specific paths
   */
  skip?: string[];

  /**
   * Log level to use
   * @default 'info'
   */
  level?: 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal';

  /**
   * Include request body in logs
   * @default false
   */
  includeBody?: boolean;

  /**
   * Include request headers in logs
   * @default false
   */
  includeHeaders?: boolean;
}

/**
 * Validation options
 */
export interface ValidationOptions {
  /**
   * Whether to use HttpError for validation errors
   * @default true
   */
  useHttpError?: boolean;

  /**
   * Whether to abort on first error
   * @default false
   */
  abortEarly?: boolean;

  /**
   * Whether to strip unknown properties
   * @default true
   */
  stripUnknown?: boolean;

  /**
   * Whether to allow unknown properties
   * @default false
   */
  allowUnknown?: boolean;
}

/**
 * Upload options
 */
export interface UploadOptions {
  /**
   * Maximum file size in bytes
   * @default 10485760 (10MB)
   */
  maxSize?: number;

  /**
   * Allowed MIME types
   */
  allowedMimeTypes?: string[];

  /**
   * Destination directory for disk storage
   * If not provided, use memory storage
   */
  destination?: string;
}

/**
 * Authentication options
 */
export interface AuthOptions {
  /**
   * Authentication type
   * @default 'jwt'
   */
  type?: 'jwt' | 'azure' | 'none' | 'custom';

  /**
   * Whether authentication is required
   * @default true
   */
  required?: boolean;

  /**
   * JWT authentication options
   */
  jwt?: {
    /**
     * JWT secret
     */
    secret?: string;

    /**
     * JWT public key for RS256
     */
    publicKey?: string;

    /**
     * JWT issuer
     */
    issuer?: string;

    /**
     * JWT audience
     */
    audience?: string;
  };

  /**
   * Azure AD authentication options
   */
  azure?: {
    /**
     * Azure AD tenant ID
     */
    tenantId?: string;

    /**
     * Azure AD client ID
     */
    clientId?: string;

    /**
     * Azure AD client secret
     */
    clientSecret?: string;

    /**
     * Azure AD API scope
     */
    apiScope?: string;
  };

  /**
   * Custom authentication function
   */
  customAuthFn?: (
    app: Application,
    config: ConfigManager,
    logger: Logger
  ) => Promise<RequestHandler>;
}

/**
 * Error codes
 */
export enum ErrorCode {
  BAD_REQUEST = 'BAD_REQUEST',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  NOT_FOUND = 'NOT_FOUND',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  CONFLICT = 'CONFLICT',
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  DATABASE_ERROR = 'DATABASE_ERROR',
  NOT_IMPLEMENTED = 'NOT_IMPLEMENTED',
  TOO_MANY_REQUESTS = 'TOO_MANY_REQUESTS',
  UPLOAD_ERROR = 'UPLOAD_ERROR',
  AUTHENTICATION_REQUIRED = 'AUTHENTICATION_REQUIRED',
  INSUFFICIENT_PERMISSIONS = 'INSUFFICIENT_PERMISSIONS'
}

/**
 * Security configuration
 */
export interface SecurityOptions {
  enabled?: boolean;
  helmet?: boolean;
  cors?: boolean;
  corsOptions?: any;
}

/**
 * API documentation configuration
 */
export interface DocsOptions {
  enabled?: boolean;
  endpoint?: string;
  title?: string;
  version?: string;
  description?: string;
}

/**
 * Complete API framework options
 */
export interface ApiFrameworkOptions {
  security?: SecurityOptions;
  errorHandling?: ErrorHandlingOptions;
  validation?: ValidationOptions;
  responseFormat?: ResponseFormatOptions;
  rateLimit?: RateLimitOptions;
  metrics?: MetricsOptions;
  correlationId?: CorrelationIdOptions;
  docs?: DocsOptions;
}

export interface AuthUser {
  id: string;
  role?: string;
  permissions?: string[];
  [key: string]: any;
}

/**
 * Declare module augmentation for Express Request
 * We use module augmentation instead of global declaration to avoid conflicts
 */
declare module 'express' {
  interface Request {
    // We use a more specific property name to avoid conflicts
    authUser?: AuthUser;
    correlationId?: string;
    auth?: Record<string, any>;
  }
}

/**
 * Options for correlation ID middleware
 */
export interface CorrelationIdOptions {
  /**
   * HTTP header to get/set correlation ID
   * @default 'X-Correlation-ID'
   */
  header?: string;

  /**
   * Property name to store correlation ID in request
   * @default 'correlationId'
   */
  propertyName?: string;

  /**
   * Whether to generate ID if not provided
   * @default true
   */
  generateId?: boolean;
}
